import React, { useState } from "react";
import { ChevronRight, ChevronDown, Target, Shield, Zap, Users, BarChart3, RefreshCw, Lightbulb, BookOpen, CheckCircle, AlertTriangle, Menu, Github, Scale, FileText, Gauge } from "lucide-react";
import "./App.css";

const PlaybookSection = ({ section, isExpanded, onToggle, depth = 0 }) => {
  const [expandedSubsections, setExpandedSubsections] = useState({});

  const toggleSubsection = (key) => {
    setExpandedSubsections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getIcon = (iconName) => {
    const icons = {
      Target,
      Shield,
      Zap,
      Users,
      BarChart3,
      RefreshCw,
      Lightbulb,
      BookOpen,
      CheckCircle,
      AlertTriangle,
      Scale,
      FileText,
      Gauge
    };
    const Icon = icons[iconName] || BookOpen;
    return <Icon size={20} />;
  };

  const getBadgeClass = (priority) => {
    switch (priority) {
      case "high": return "badge badge-orange";
      case "medium": return "badge badge-blue";
      case "low": return "badge badge-green";
      default: return "badge badge-blue";
    }
  };

  return (
    <div className={`playbook-section depth-${depth}`}>
      <div className="section-header" onClick={onToggle}>
        <div className="section-title">
          {section.icon && getIcon(section.icon)}
          <h3>{section.title}</h3>
          {section.priority && (
            <span className={getBadgeClass(section.priority)}>
              {section.priority}
            </span>
          )}
          {section.timeline && (
            <span className="badge badge-purple">
              {section.timeline}
            </span>
          )}
        </div>
        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </div>

      {isExpanded && (
        <div className="section-content">
          {section.description && (
            <p className="section-description">{section.description}</p>
          )}

          {section.keyPoints && (
            <div className="key-points">
              <h4>Key Points:</h4>
              <ul>
                {section.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          )}

          {section.actions && (
            <div className="actions">
              <h4>Actions:</h4>
              <div className="action-grid">
                {section.actions.map((action, index) => (
                  <div key={index} className="action-card">
                    <div className="action-header">
                      <CheckCircle size={16} />
                      <span>{action.title}</span>
                    </div>
                    <p className="text-sm text-gray-600">{action.description}</p>
                    {action.deliverable && (
                      <div className="deliverable">
                        <strong>Deliverable:</strong> {action.deliverable}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {section.subsections && (
            <div className="subsections">
              {section.subsections.map((subsection, index) => {
                const key = `${section.title}-${index}`;
                return (
                  <PlaybookSection
                    key={key}
                    section={subsection}
                    isExpanded={expandedSubsections[key]}
                    onToggle={() => toggleSubsection(key)}
                    depth={depth + 1}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const App = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [activePhase, setActivePhase] = useState("all");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const playbookData = [
    {
      title: "Policy Foundations & Philosophical Framework",
      icon: "Scale",
      priority: "high",
      timeline: "Month 1",
      description: "Establish the fundamental policy principles and philosophical conditions that govern organizational adoption of advanced technologies",
      keyPoints: [
        "Regulatory Compliance: EU AI Act, UK AI Principles, and sector-specific regulations",
        "Ethical Foundations: Human dignity, autonomy, justice, and beneficence principles",
        "Risk-Based Classification: EU four-tier risk assessment framework (Unacceptable, High, Limited, Minimal)",
        "Transparency Requirements: Algorithmic accountability and explainability standards",
        "Democratic Values: Protection of fundamental rights and democratic processes"
      ],
      actions: [
        {
          title: "Establish Policy Charter",
          description: "Develop comprehensive policy framework aligned with regulatory requirements",
          deliverable: "Organizational AI Policy Charter and Compliance Framework"
        },
        {
          title: "Philosophical Alignment Assessment",
          description: "Evaluate organizational values against ethical AI principles",
          deliverable: "Values Alignment Report and Gap Analysis"
        }
      ]
    },
    {
      title: "Stanford Measurement Framework & Model Qualification",
      icon: "Gauge",
      priority: "high",
      timeline: "Month 2-3",
      description: "Implement Stanford evidence-based approach to AI system measurement, evaluation, and qualification using standardized benchmarks and assessment methodologies",
      subsections: [
        {
          title: "BetterBench Assessment Protocol",
          keyPoints: [
            "Comprehensive Evaluation: Multi-dimensional assessment across accuracy, fairness, toxicity, efficiency, and robustness",
            "HELM Framework: Holistic evaluation of language models with scenario-based testing",
            "Benchmark Quality Assurance: Minimum standards checklist for evaluation reliability",
            "Test-Time Compute Evaluation: Assessment of iterative reasoning capabilities",
            "Domain-Specific Benchmarks: RE-Bench for complex agent tasks, SWE-bench for coding capabilities"
          ],
          actions: [
            {
              title: "Implement HELM Evaluation",
              description: "Deploy Stanford Holistic Evaluation framework for systematic model assessment",
              deliverable: "HELM Assessment Report and Model Performance Baselines"
            }
          ]
        }
      ]
    }
  ];

  const phases = ["all", "foundation", "pilot", "scale"];

  const filteredSections = activePhase === "all"
    ? playbookData
    : playbookData.filter(section => {
        if (activePhase === "foundation") return section.timeline?.includes("Month 1") || section.timeline?.includes("Month 2") || section.timeline?.includes("Month 3");
        if (activePhase === "pilot") return section.timeline?.includes("Month 4") || section.timeline?.includes("Month 6");
        if (activePhase === "scale") return section.timeline?.includes("Month 10") || section.timeline?.includes("Ongoing");
        return true;
      });

  return (
    <div className="app">
      <nav className="top-nav">
        <div className="container">
          <div className="nav-content">
            <div className="nav-brand">
              <BookOpen size={24} />
              <span>Enterprise Technology Playbook</span>
            </div>
            <div className="nav-links">
              <button className="nav-link active">
                <Target size={18} />
                AI Playbook
              </button>
              <button className="nav-link">
                <Github size={18} />
                GitHub
              </button>
            </div>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu size={24} />
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="mobile-menu">
              <button className="nav-link active">
                <Target size={18} />
                AI Playbook
              </button>
              <button className="nav-link">
                <Github size={18} />
                GitHub
              </button>
            </div>
          )}
        </div>
      </nav>

      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="header-text">
              <h1>Advanced Technology Adoption Framework</h1>
              <p>Evidence-based organizational playbook incorporating EU regulatory compliance, Stanford measurement frameworks, and institutional governance best practices</p>
            </div>
            <div className="header-stats">
              <div className="stat">
                <div className="stat-number">9</div>
                <div className="stat-label">Core Sections</div>
              </div>
              <div className="stat">
                <div className="stat-number">18+</div>
                <div className="stat-label">Months Timeline</div>
              </div>
              <div className="stat">
                <div className="stat-number">75+</div>
                <div className="stat-label">Action Items</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="phase-nav">
        <div className="container">
          <div className="phase-buttons">
            {phases.map(phase => (
              <button
                key={phase}
                className={`btn ${activePhase === phase ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setActivePhase(phase)}
              >
                {phase.charAt(0).toUpperCase() + phase.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="main-content">
        <div className="container">
          <div className="playbook-sections">
            {filteredSections.map((section, index) => {
              const key = `section-${index}`;
              return (
                <PlaybookSection
                  key={key}
                  section={section}
                  isExpanded={expandedSections[key]}
                  onToggle={() => toggleSection(key)}
                />
              );
            })}
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Regulatory Frameworks</h4>
              <ul>
                <li>EU AI Act (Regulation 2024/1689)</li>
                <li>UK Government AI Principles</li>
                <li>Stanford Measurement Standards</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Measurement Protocols</h4>
              <ul>
                <li>Stanford BetterBench</li>
                <li>HELM Evaluation Framework</li>
                <li>EU GPAI Compliance Standards</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Implementation Timeline</h4>
              <ul>
                <li>Policy Foundation: Month 1</li>
                <li>Governance Setup: Month 2-3</li>
                <li>Full Deployment: 18+ months</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>Professional framework based on empirical research and regulatory compliance requirements</p>
            <p className="text-sm text-gray-500">Framework version 2.0 | Updated September 2024</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
